import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { InputType } from '@core/enums/input-type';
import { ToastService } from '@core/services/toast.service';
import { switchMap, take, filter } from 'rxjs';
import { ResourceService } from '../../services/resource.service';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { NzModalService } from 'ng-zorro-antd/modal'; // 🔹 add this

@Component({
  selector: 'prism-facilitation-attachment',
  templateUrl: './facilitation-attachment.component.html',
  styleUrl: './facilitation-attachment.component.scss'
})
export class FacilitationAttachmentComponent {
  private fb = inject(FormBuilder);
  private resourceService = inject(ResourceService);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private modal = inject(NzModalService);  

  @Input() guideId: number = 0;
  @Input() isEdit = false;
  @Output() closeAttachment = new EventEmitter();
  @Output() updateAttachments = new EventEmitter();

  attachmentForm: FormGroup | any;
  InputType = InputType;
  selectedFile: File | null = null;
  isUploading = false;
  filePickerDisabled = false;

  private uploadTimeoutId: any;
  private beforeUnloadHandler = (event: BeforeUnloadEvent) => {
    if (this.isUploading) {
      event.preventDefault();

      (event as any).returnValue = '';
    }
  };


  constructor() {}

  ngOnInit(): void {
    this.initForm();
    this.attachmentForm.get('fileName').disable({ emitEvent: false });
  }

  initForm(): void {
    this.attachmentForm = this.fb.group({
      id: [0],
      fileName: [null, Validators.required]
    });
  }

  fileChange(event: any) {
    const files =
      event?.file?.target?.files ||
      event?.target?.files ||
      event?.file?.files ||
      event?.files;

    if (!files || !files.length) {
      this.attachmentForm.get('fileName')!.disable({ emitEvent: false });
      this.selectedFile = null;
      return;
    }

    const file = files[0];
    const maxSizeBytes = 500 * 1024 * 1024; // 500MB

    if (file.size > maxSizeBytes) {
      this.toastService.error('The file size exceeds the 500MB max allowed.');
      this.selectedFile = null;
      this.attachmentForm.reset({ id: 0, fileName: null });
      this.attachmentForm.get('fileName')!.disable({ emitEvent: false });
      return;
    }

    this.selectedFile = file;

    this.attachmentForm.patchValue({ fileName: file.name });
    this.attachmentForm.get('fileName')!.markAsDirty();
    this.attachmentForm.get('fileName')!.enable({ emitEvent: false });
  }

  submitResource(): void {
    if (!this.selectedFile) {
      this.toastService.error('Please browse and select a file first.');
      return;
    }

    const editedTitle = (this.attachmentForm.get('fileName')?.value || '').trim();
    if (!editedTitle) {
      this.toastService.error('Title cannot be empty.');
      return;
    }
    const file = this.selectedFile;

    const dto = {
      ...this.attachmentForm.getRawValue(),
      fileName: editedTitle,
      courseId: this.guideId
    };

    this.isUploading = true;
    this.filePickerDisabled = true;

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    if (this.uploadTimeoutId) {
      clearTimeout(this.uploadTimeoutId);
    }

    this.uploadTimeoutId = setTimeout(() => {
      if (this.isUploading) {
        this.toastService.info(
          'This looks like a large file and may take a bit longer to upload. Please wait…'
        );
      }
    }, 10000);

    this.resourceService
      .getAttachmentUploadSas({ fileName: file.name, courseId: this.guideId })
      .pipe(
        take(1),
        switchMap(sas => {
          return this.resourceService.uploadFileToBlob(sas.uploadUrl, file).pipe(
            filter(event => event.type === HttpEventType.Response),
            take(1),
            switchMap(() => {
              const metadataDto = {
                ...dto,
                blobUrl: sas.blobUrl
              };
              return this.resourceService.saveAttachmentMetadata(metadataDto).pipe(take(1));
            })
          );
        })
      )
      .subscribe({
        next: (ok: boolean) => {
          this.isUploading = false;
          this.filePickerDisabled = false;

          if (typeof window !== 'undefined') {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
          }

          if (this.uploadTimeoutId) {
            clearTimeout(this.uploadTimeoutId);
            this.uploadTimeoutId = null;
          }

          if (ok) {
            this.toastService.success('Attachment added successfully');
            this.updateAttachments.emit();
            this.resetForm();
            this.closeForm();
          } else {
            this.toastService.error('Upload failed');
          }
        },
        error: (error: HttpErrorResponse) => {
          this.isUploading = false;
          this.filePickerDisabled = false;

          if (typeof window !== 'undefined') {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
          }

          if (this.uploadTimeoutId) {
            clearTimeout(this.uploadTimeoutId);
            this.uploadTimeoutId = null;
          }

          console.error('Upload error:', error);

          if (error.status === 413) {
            this.toastService.error('The file size exceeds the allowed max on the storage endpoint.');
            return;
          }

          this.toastService.error('Error during upload');
        }
      });
  }

  private resetForm(): void {
    this.selectedFile = null;
    this.attachmentForm.reset({ id: 0, fileName: null });
    this.attachmentForm.get('fileName')!.disable({ emitEvent: false });
  }

  closeForm(): void {
    this.resetForm();
    this.closeAttachment.emit();
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }
}